import { Environment } from '@/internals/env.config';
import { APP_STORE_LINK, PLAY_STORE_LINK } from '@/ui/utils/externalLinks';

import { makeSchemaTarget } from './makeSchemaTarget';

export type Intent = 'get-app' | 'open-app';

interface GenerateLinkParams {
  referer: string;
  device: DeviceType;
  linkFallback?: string;
  intent: Intent;
  env: Environment;
}

interface GenerateLinkResult {
  href: string;
  clipboardTarget?: string;
}

export enum DeviceType {
  IOS = 'ios',
  ANDROID = 'android',
  WEB = 'web',
}

export function getDeviceType(ua: string): DeviceType {
  const lowerCaseUa = ua.toLowerCase();
  if (/(ipad|iphone|ipod|fxios)/gi.test(lowerCaseUa)) {
    return DeviceType.IOS;
  } else if (/android/gi.test(lowerCaseUa)) {
    return DeviceType.ANDROID;
  } else {
    return DeviceType.WEB;
  }
}

export function resolveInviteCta({
  env,
  device,
  referer,
  linkFallback,
  intent,
}: GenerateLinkParams): GenerateLinkResult {
  const isIOS = device === DeviceType.IOS;
  const isAndroid = device === DeviceType.ANDROID;

  const inviteUrl = getMaybeUrl(referer) || getMaybeUrl(linkFallback || '');

  // "Already have the app?" — open the invite directly in the app.
  // iOS uses the custom scheme; Android and web both fall back to the web app.
  if (intent === 'open-app') {
    const schemaDevice = isIOS ? DeviceType.IOS : DeviceType.WEB;
    const href = makeSchemaTarget(env, schemaDevice, inviteUrl?.toString()) || env.config.APP_ORIGIN;
    return { href };
  }

  // "Download" — send the user to the relevant store (or the web app on desktop).
  let targetRaw: string;
  switch (device) {
    case DeviceType.ANDROID:
      targetRaw = PLAY_STORE_LINK;
      break;
    case DeviceType.IOS:
      targetRaw = APP_STORE_LINK;
      break;
    case DeviceType.WEB:
    default:
      targetRaw = makeSchemaTarget(env, DeviceType.WEB, inviteUrl?.toString()) || env.config.APP_ORIGIN;
      break;
  }

  let targetHref: string;
  try {
    let targetUrl = new URL(targetRaw);
    targetUrl = withUtmParameters(inviteUrl, targetUrl) || targetUrl;

    if (isAndroid) {
      const encodedReferrer = encodeURI(`utm_source=${inviteUrl?.toString() || ''}`);
      targetUrl.searchParams.append('referrer', encodedReferrer);
    }

    targetHref = targetUrl.toString();
  } catch {
    targetHref = targetRaw;
  }

  const clipboardTarget = isIOS ? inviteUrl?.toString() : undefined;

  return { href: targetHref, clipboardTarget };
}

function withUtmParameters(sourceURL: URL | undefined, targetURL: URL): URL | undefined {
  if (!sourceURL) return;

  const sourceParams = new URLSearchParams(sourceURL.search);
  const targetParams = new URLSearchParams(targetURL.search);

  for (const [key, value] of sourceParams) {
    if (key.startsWith('utm_')) {
      targetParams.set(key, value);
    }
  }

  const resultTargetUrl = new URL(targetURL);
  resultTargetUrl.search = targetParams.toString();

  return resultTargetUrl;
}

const getMaybeUrl = (url: string): URL | undefined => {
  try {
    const result = new URL(url);
    return result;
  } catch (_) {
    return undefined;
  }
};
